<template>
  <v-container fluid class="pa-4 dashboard-container">
    <!-- Loading overlay -->
    <div v-if="refreshLoading" class="loader-overlay">
      <v-card class="pa-6 text-center" elevation="8">
        <img src="@/assets/loader.gif" alt="Loading..." class="loader-gif" />
        <div class="mt-2 text-body-2 grey--text">Chargement des données...</div>
      </v-card>
    </div>

    <!-- Unified Toolbar -->
    <DashboardToolbar
      :totalCount="selectedKiosks.length"
      :filteredCount="filteredKiosks.length"
      :alertCounts="alertCounts"
      :date.sync="date"
      :time.sync="time"
      :search.sync="search"
      :selectedTags.sync="selectedTags"
      :availableTags="availableTags"
      :availableReports="availableReports"
      :ticketFilter.sync="ticketFilter"
      :selectedFilters.sync="selectedFilters"
      :useXorMode.sync="useXorMode"
      :loading="refreshLoading"
      @time-select="handleTimeSelect"
      @refresh-tickets="refreshTickets"
      @open-refresh-dialog="refreshDialog = true"
      @download="downloadData"
      @open-info-dialog="infoDialog = true"
      @clear-filters="clearFilters"
    />

    <!-- Data table -->
    <DashboardTable
      :items="filteredKiosks"
      :search="search"
      @filtered-items="filteredItems = $event"
      @open-detail="openDetailDialog"
    />

    <!-- Kiosk Detail Dialog -->
    <KioskDetailDialog
      v-model="detailDialog"
      :kiosk="selectedDetailKiosk"
      @open-comment="openCommentFromDetail"
    />

    <!-- All dialogs -->
    <DashboardDialogs
      :commentDialog.sync="commentDialog"
      :currentComment.sync="currentComment"
      :comments="comments"
      :saveCommentEnabled="saveCommentEnabled"
      :refreshDialog.sync="refreshDialog"
      :infoDialog.sync="infoDialog"
      :apiStatuses="apiStatuses"
      :loadingStatuses="loadingStatuses"
      :errorDialog.sync="errorDialog"
      @save-comment="saveComment"
      @confirm-refresh="refreshData"
    />
  </v-container>
</template>

<script>
import { store } from "@/models/Store.js";
import DashboardService from "@/services/DashboardService.js";
import { ALERT_FILTERS, API_STATUS_ENDPOINTS, filterKiosks } from "@/constants/dashboard.constants.js";
import DashboardToolbar from "@/components/dashboard/DashboardToolbar.vue";
import DashboardTable from "@/components/dashboard/DashboardTable.vue";
import DashboardDialogs from "@/components/dashboard/DashboardDialogs.vue";
import KioskDetailDialog from "@/components/dashboard/KioskDetailDialog.vue";

export default {
  name: 'DashboardView',
  components: {
    DashboardToolbar,
    DashboardTable,
    DashboardDialogs,
    KioskDetailDialog
  },
  data: () => ({
    store,
    // Filters
    date: null,
    time: null,
    search: '',
    selectedTags: [],
    availableTags: [],
    availableReports: [],
    ticketFilter: 'all',
    selectedFilters: [],
    useXorMode: false,
    filteredItems: [],
    // Data
    selectedKiosks: [],
    selectedReportId: null,
    // Comment dialog
    selectedKiosk: null,
    comments: [],
    commentDialog: false,
    currentComment: '',
    saveCommentEnabled: true,
    // Detail dialog
    detailDialog: false,
    selectedDetailKiosk: null,
    // Other dialogs
    refreshDialog: false,
    infoDialog: false,
    errorDialog: false,
    // Loading states
    refreshLoading: false,
    loadingStatuses: false,
    // API statuses
    apiStatuses: API_STATUS_ENDPOINTS.map(api => ({ ...api, status: 'Hors ligne' })),
    // Constants
    availableFilters: ALERT_FILTERS
  }),

  async mounted() {
    await this.fetchFilters();
    await this.fetchKioskData();
  },

  computed: {
    filteredKiosks() {
      return filterKiosks(this.selectedKiosks, {
        ticketFilter: this.ticketFilter,
        selectedFilters: this.selectedFilters,
        useXorMode: this.useXorMode,
        selectedTags: this.selectedTags
      });
    },
    alertCounts() {
      const counts = {};
      const alertKeys = ['salesAlert', 'tpaAlert', 'pingAlert', 'cancelledSalesAlert', 'rackAlert', 'voltageAlert', 'motherboardFreezeAlert', 'fuseAlert', 'batteryAlert'];
      alertKeys.forEach(key => {
        counts[key] = this.selectedKiosks.filter(k => k[key]).length;
      });
      return counts;
    }
  },

  watch: {
    infoDialog(val) {
      if (val) {
        this.fetchApiStatuses();
      }
    }
  },

  methods: {
    // Data fetching
    async fetchFilters() {
      try {
        this.availableReports = await DashboardService.fetchFilters();
      } catch (error) {
        this.errorDialog = true;
        console.error('Error fetching filters:', error);
      }
    },

    async fetchKioskData(id = null) {
      this.refreshLoading = true;
      try {
        const data = await DashboardService.fetchKioskData(id);
        this.selectedReportId = data._id;
        this.selectedKiosks = data.content;
        this.availableTags = data.availableTags;
        this.date = new Date(data.date).toISOString().substring(0, 10);
        this.time = data._id;
      } catch (error) {
        this.errorDialog = true;
        console.error('Error fetching kiosk data:', error);
      }
      this.refreshLoading = false;
    },

    async handleTimeSelect(selectedId) {
      if (!selectedId) return;
      this.selectedReportId = selectedId;
      await this.fetchKioskData(selectedId);
    },

    // Refresh actions
    async refreshData() {
      this.refreshDialog = false;
      this.refreshLoading = true;
      try {
        const data = await DashboardService.refreshDashboard();
        this.selectedKiosks = data.content;
        this.date = new Date(data.date).toISOString().substring(0, 10);
        this.time = data._id;
      } catch (error) {
        this.errorDialog = true;
        console.error('Error refreshing dashboard:', error);
      }
      this.refreshLoading = false;
    },

    async refreshTickets() {
      this.refreshLoading = true;
      try {
        await DashboardService.refreshTickets(this.selectedReportId);
        await this.fetchKioskData(this.selectedReportId);
      } catch (error) {
        this.errorDialog = true;
        console.error('Error refreshing tickets:', error);
      }
      this.refreshLoading = false;
    },

    // Download
    async downloadData() {
      try {
        const blob = await DashboardService.exportDashboard(
          this.selectedReportId,
          this.filteredKiosks.map(k => k.id)
        );
        DashboardService.downloadBlob(
          new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          'dashboard_export.xlsx'
        );
      } catch (error) {
        this.errorDialog = true;
        console.error('Error downloading data:', error);
      }
    },

    // Comments
    async openCommentDialog(kiosk) {
      this.selectedKiosk = kiosk;
      const kioskId = kiosk.id.replace(/WG/g, '').replace(/^0+/, '');

      try {
        this.comments = await DashboardService.fetchComments(kioskId);
        this.saveCommentEnabled = true;
      } catch (error) {
        if (error.response?.status === 404) {
          this.saveCommentEnabled = false;
          this.comments = [];
        } else {
          this.errorDialog = true;
          console.error('Error fetching comments:', error);
        }
      }
      this.commentDialog = true;
    },

    async saveComment() {
      const kioskId = this.selectedKiosk.id.replace(/WG/g, '').replace(/^0+/, '');
      try {
        this.comments = await DashboardService.saveComment(
          kioskId,
          this.store.state.currentUsername,
          this.currentComment
        );
        this.currentComment = '';
      } catch (error) {
        this.errorDialog = true;
        console.error('Error saving comment:', error);
      }
    },


    // Detail dialog
    openDetailDialog(kiosk) {
      this.selectedDetailKiosk = kiosk;
      this.detailDialog = true;
    },

    openCommentFromDetail(kiosk) {
      this.detailDialog = false;
      this.openCommentDialog(kiosk);
    },

    // Clear filters
    clearFilters() {
      this.search = '';
      this.selectedTags = [];
      this.ticketFilter = 'all';
      this.selectedFilters = [];
      this.useXorMode = false;
    },

    // API statuses
    async fetchApiStatuses() {
      this.loadingStatuses = true;
      this.apiStatuses = await DashboardService.checkApiStatuses(API_STATUS_ENDPOINTS);
      this.loadingStatuses = false;
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loader-gif {
  width: 150px;
  height: 150px;
}
</style>
